/**
 * Client-side table export.
 *
 * Turns an array of row objects + a column config into a downloadable
 * file in the chosen format. Every format is generated in the browser
 * — no server round-trip — except the two heavy binary formats
 * (Parquet, ZIP) which can't be produced without a real encoder; those
 * return `{ queued: true }` so the caller can show a "queued & emailed"
 * message instead of a download, mirroring the table standard.
 *
 * Column contract (subset of the DataTable column shape):
 *   { key, header, exportValue?(row) => string|number }
 * `exportValue` lets a column export a plain value even when its cell
 * renders a pill / avatar / node. Falls back to `row[key]`.
 */

import { buildXlsx } from './xlsx.js'
import { formatDateTime, formatNumber } from '../../lib/format.js'

/** Resolve the raw export text for one cell. A Date object reads as the
 *  person sees dates on screen (their zone and formats); strings — e.g. an
 *  ISO timestamp a column deliberately exports — pass through untouched. */
function cellText(row, col) {
  const v = cellValue(row, col)
  if (v == null) return ''
  if (v instanceof Date) return formatDateTime(v)
  return String(v)
}

/** The RAW value, before stringification. The XLSX encoder needs this: once a
 *  number or a date has been through String() its type is gone, and a number
 *  typed as text does not sum in Excel. */
function cellValue(row, col) {
  if (typeof col.exportValue === 'function') return col.exportValue(row)
  return row[col.key]
}

/**
 * Neutralise spreadsheet formula injection (CWE-1236). A delimited-text cell
 * that starts with = + - @ (or a tab / CR) is EVALUATED when the file is opened
 * in Excel / LibreOffice / Sheets — and exported cells carry text other people
 * typed (task names, comments, a visitor's referrer or user-agent), so whoever
 * opens the export runs the author's formula. A leading apostrophe makes the
 * spreadsheet treat the cell as literal text; same guard MemberImportDialog's
 * error report already applies. A plain signed number or percentage ("-5",
 * "+3.2", "-12%") is left alone so numeric columns still sum.
 *
 * Only for delimited text (CSV / TSV / clipboard). The .xlsx encoder does not
 * need it: it writes typed inline-string cells, which are never evaluated.
 */
export function neutraliseFormula(value) {
  const s = String(value ?? '')
  if (!/^[=+\-@\t\r]/.test(s)) return s
  if (/^[+-]?\d+(\.\d+)?%?$/.test(s)) return s
  return `'${s}`
}

/** RFC-4180 CSV field: wrap in quotes and double any embedded quote
 *  whenever the value contains a delimiter, quote or newline. */
function csvField(raw, delim) {
  if (raw === '') return ''
  const s = neutraliseFormula(raw)
  if (
    s.includes(delim) ||
    s.includes('"') ||
    s.includes('\n') ||
    s.includes('\r')
  ) {
    return '"' + s.replace(/"/g, '""') + '"'
  }
  return s
}

const xmlEsc = (s) =>
  String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')

const htmlEsc = (s) =>
  String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')

/** A safe XML/SQL identifier from a column key. */
const ident = (k) => String(k).replace(/[^A-Za-z0-9_]/g, '_')

/** SQL string literal — single-quote escaped. */
const sqlLit = (s) => "'" + String(s).replace(/'/g, "''") + "'"

function toDelimited(rows, cols, delim) {
  const head = cols.map((c) => csvField(c.header, delim)).join(delim)
  const body = rows
    .map((r) => cols.map((c) => csvField(cellText(r, c), delim)).join(delim))
    .join('\r\n')
  return head + '\r\n' + body + '\r\n'
}

function toJson(rows, cols) {
  return JSON.stringify(
    rows.map((r) => {
      const o = {}
      for (const c of cols) o[c.key] = cellText(r, c)
      return o
    }),
    null,
    2,
  )
}

function toNdjson(rows, cols) {
  return rows
    .map((r) => {
      const o = {}
      for (const c of cols) o[c.key] = cellText(r, c)
      return JSON.stringify(o)
    })
    .join('\n')
}

function toXml(rows, cols) {
  const lines = ['<?xml version="1.0" encoding="UTF-8"?>', '<records>']
  for (const r of rows) {
    lines.push('  <record>')
    for (const c of cols)
      lines.push(
        `    <${ident(c.key)}>${xmlEsc(cellText(r, c))}</${ident(c.key)}>`,
      )
    lines.push('  </record>')
  }
  lines.push('</records>')
  return lines.join('\n')
}

function toSql(rows, cols, table) {
  const t = ident(table || 'export')
  const colList = cols.map((c) => ident(c.key)).join(', ')
  const create =
    `CREATE TABLE ${t} (\n` +
    cols.map((c) => `  ${ident(c.key)} TEXT`).join(',\n') +
    '\n);\n'
  const inserts = rows
    .map(
      (r) =>
        `INSERT INTO ${t} (${colList}) VALUES (` +
        cols.map((c) => sqlLit(cellText(r, c))).join(', ') +
        ');',
    )
    .join('\n')
  return create + inserts + '\n'
}

function toMarkdown(rows, cols) {
  const esc = (s) => String(s).replace(/\|/g, '\\|').replace(/\n/g, ' ')
  const head = '| ' + cols.map((c) => esc(c.header)).join(' | ') + ' |'
  const rule = '| ' + cols.map(() => '---').join(' | ') + ' |'
  const body = rows
    .map((r) => '| ' + cols.map((c) => esc(cellText(r, c))).join(' | ') + ' |')
    .join('\n')
  return [head, rule, body].join('\n') + '\n'
}

function toHtmlTable(rows, cols) {
  const th = cols.map((c) => `<th>${htmlEsc(c.header)}</th>`).join('')
  const trs = rows
    .map(
      (r) =>
        '<tr>' +
        cols.map((c) => `<td>${htmlEsc(cellText(r, c))}</td>`).join('') +
        '</tr>',
    )
    .join('\n')
  return `<table><thead><tr>${th}</tr></thead><tbody>\n${trs}\n</tbody></table>`
}

/** Standalone styled HTML page (for the HTML format download and the
 *  PDF print window). */
function toHtmlDoc(rows, cols, title) {
  return `<!doctype html><html><head><meta charset="utf-8"><title>${htmlEsc(
    title,
  )}</title><style>
    body{font-family:system-ui,Segoe UI,Arial,sans-serif;color:#0f172a;margin:24px}
    h1{font-size:18px;margin:0 0 4px}
    p.meta{color:#64748b;font-size:12px;margin:0 0 16px}
    table{border-collapse:collapse;width:100%;font-size:12px}
    th,td{border:1px solid #e2e8f0;padding:6px 10px;text-align:left;vertical-align:top}
    thead th{background:#f8fafc;position:sticky;top:0}
    tbody tr:nth-child(even){background:#fafbfc}

    /* Print / PDF layout. The format note promises "print layout, repeating
       header" and nothing here delivered one: past page 1 the reader met
       unlabelled columns, and rows split across the page break (QA-173).
       display:table-header-group is what makes a thead repeat on every
       printed page; position:sticky is a screen-only affordance and does
       nothing here, so it is dropped for print.
       (No backticks in this comment - the whole block sits inside a JS
       template literal, and one would end the string.) */
    @media print {
      @page { margin: 14mm; }
      body { margin: 0; }
      h1 { margin-bottom: 2px; }
      thead { display: table-header-group; position: static; }
      tfoot { display: table-footer-group; }
      tr { break-inside: avoid; page-break-inside: avoid; }
      thead th { position: static; background: #f8fafc;
                 /* Backgrounds are dropped by default in print. */
                 -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      tbody tr:nth-child(even) { background: #fafbfc;
                 -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
  </style></head><body>
    <h1>${htmlEsc(title)}</h1>
    <p class="meta">${formatNumber(rows.length)} records &middot; ${formatNumber(cols.length)} columns</p>
    ${toHtmlTable(rows, cols)}
  </body></html>`
}

/** Trigger a browser download of `content` as `filename`. */
function download(content, filename, mime) {
  const blob =
    content instanceof Blob ? content : new Blob([content], { type: mime })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  // Revoke on the next tick so the click has committed first.
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

/** Format registry — label, extension, note and whether it downloads
 *  immediately. Drives the export modal grid.
 *
 *  Every format here is `true`. Parquet and ZIP used to sit alongside them
 *  with `immediate: false`, and `runExport` answered `{ queued: true }` —
 *  which did nothing at all: no request, no job, no mail. The table then
 *  told the user they would be emailed a file that nothing could produce
 *  (defect2 D2). They are gone until a server-side exporter exists; the
 *  flag stays so adding one back is a one-line change here plus a real
 *  handler below. */
export const EXPORT_FORMATS = [
  { id: 'CSV', ext: '.csv', note: 'Comma-separated. Opens anywhere. No formatting, no formulas.', immediate: true },
  { id: 'XLSX', ext: '.xlsx', note: 'Excel workbook. Numbers and dates keep their types; the header row freezes and filters.', immediate: true },
  { id: 'PDF', ext: '.pdf', note: 'Print layout, repeating header. Read-only — not for re-import.', immediate: true },
  { id: 'JSON', ext: '.json', note: 'One array of objects. Column keys, not labels. For re-import and APIs.', immediate: true },
]

/* Deliberately FOUR formats — CSV, XLSX, PDF, JSON — product decision, applied
 * everywhere at once: this registry is the only thing that draws the export
 * modal's grid, and every table in every panel renders that one modal.
 *
 * TSV, NDJSON, XML, HTML, Markdown and SQL were dropped from the grid. Their
 * encoders and `switch` arms below are intentionally LEFT IN PLACE: they work,
 * they are covered by the same code path, and keeping them makes restoring a
 * format a one-line addition here rather than a rewrite. Nothing can reach
 * them while they are absent from this list — `runExport` looks the id up in
 * `EXPORT_FORMAT_MAP` first and throws on a miss — so the grid and the engine
 * cannot drift apart.
 *
 * Direct `runExport(...)` callers outside the modal (Space Overview projects,
 * Goals, Leaderboard) all pass 'CSV', which stays. `VisitorLogsPage` has its
 * own separate server-side export (csv / xlsx / pdf) and is unaffected. */

export const EXPORT_FORMAT_MAP = Object.fromEntries(
  EXPORT_FORMATS.map((f) => [f.id, f]),
)

/**
 * Run an export. Returns `{ downloaded: true, filename }` on success, or
 * `{ unsupported: true, format }` if the id has no encoder — nothing is
 * queued or emailed anywhere, so never tell the user that it is.
 *
 * @param {string}   format    One of EXPORT_FORMATS ids.
 * @param {object[]} rows      Rows to export (already scoped by caller).
 * @param {object[]} cols      Column config subset (already scoped).
 * @param {object}   [opts]
 * @param {string}   [opts.baseName='export'] File name stem.
 * @param {string}   [opts.title='Export']    Human title (HTML/PDF).
 * @param {string}   [opts.isoDate]           YYYY-MM-DD stamp; caller
 *                                            supplies it (Date is not
 *                                            deterministic in workflows).
 */
export function runExport(format, rows, cols, opts = {}) {
  const fmt = EXPORT_FORMAT_MAP[format]
  if (!fmt) throw new Error(`Unknown export format: ${format}`)
  const baseName = opts.baseName || 'export'
  const title = opts.title || 'Export'
  const stamp = opts.isoDate ? `-${opts.isoDate}` : ''
  const filename = `${baseName}${stamp}${fmt.ext}`

  // Defensive: no shipped format sets this, and nothing here can hand work
  // to a server. Report it as unsupported rather than silently succeeding.
  if (!fmt.immediate) return { unsupported: true, format }

  switch (format) {
    case 'CSV':
      download('\uFEFF' + toDelimited(rows, cols, ','), filename, 'text/csv;charset=utf-8')
      break
    case 'TSV':
      download(toDelimited(rows, cols, '\t'), filename, 'text/tab-separated-values')
      break
    case 'JSON':
      download(toJson(rows, cols), filename, 'application/json')
      break
    case 'NDJSON':
      download(toNdjson(rows, cols), filename, 'application/x-ndjson')
      break
    case 'XML':
      download(toXml(rows, cols), filename, 'application/xml')
      break
    case 'SQL':
      download(toSql(rows, cols, baseName), filename, 'application/sql')
      break
    case 'Markdown':
      download(toMarkdown(rows, cols), filename, 'text/markdown')
      break
    case 'HTML':
      download(toHtmlDoc(rows, cols, title), filename, 'text/html')
      break
    case 'XLSX': {
      // A REAL .xlsx. This used to be an HTML table written out with a .xls
      // extension: Excel opened it, but behind "The file format and extension
      // don't match. The file could be corrupted or unsafe" — not "opens
      // cleanly" (QA-172) — and every cell arrived as General text, so numbers
      // did not sum and dates did not sort. `buildXlsx` writes the genuine
      // format with per-cell types.
      download(
        buildXlsx(
          cols.map((c) => c.header),
          rows.map((r) => cols.map((c) => cellValue(r, c))),
          title,
        ),
        filename,
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      )
      break
    }
    case 'PDF': {
      // No client PDF encoder — open a print-ready window and let the
      // user "Save as PDF". Read-only, matches the standard's note.
      const w = window.open('', '_blank')
      if (w) {
        w.document.write(toHtmlDoc(rows, cols, title))
        w.document.close()
        w.focus()
        setTimeout(() => w.print(), 300)
      }
      break
    }
    default:
      return { unsupported: true, format }
  }
  return { downloaded: true, filename }
}
